import cx from 'classnames';
import { Map } from 'immutable';
import styled from '@emotion/styled';
import { useCounter } from 'react-use';
import useInterval from '@use-it/interval';
import useHotkeys from '@reecelucas/react-use-hotkeys';
import {
  debounce,
  range,
  partial,
  get,
  throttle,
  reduce,
  isBoolean,
} from 'lodash';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  createRef,
} from 'react';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';

import Step from './Typeform/Step';
import TypeformContext from './Typeform/TypeformContext';
import AnimatedChild from './Typeform/AnimatedChild';

import useEventListener from '~/react-hooks/use-event-listener';
import { useMedia } from 'react-use-media';
import useStepContext from './Typeform/useStepContext';
import ProgressBarWithNav from '~/App/Components/Typeform/ProgressBarWithNav';
import FullWidthProgressBar from '~/App/Components/Typeform/FullWidthProgressBar';

import useScrollDirection from '~/react-hooks/use-scroll-direction';

export { Step, useStepContext };

export enum ProgressBarVariant {
  Default,
  FullWidth,
}

export enum Transition {
  Default,
  FadeIn,
}

interface FormContainerProps {
  isLimitedHeight?: boolean;
  className?: string;
}
const FormContainer = styled.div<FormContainerProps>`
  height: ${(props) => (props.isLimitedHeight ? 'auto' : '100%')};
  margin: 0 auto;

  *[disabled] > * {
    user-select: none;
    pointer-events: none;
  }
`;

export type TypeformProps = {
  children: React.ReactNodeArray;
  hideProgress?: boolean;
  progressBarVariant?: ProgressBarVariant;
  containerClassName?: string;
  className?: string;
  childClassName?: string;
  defaultIndex?: number;
  showBonjoroBadge?: boolean;
  onBonjoroBadgeClick?: () => void;
  transition?: Transition;
  disableTabHotkey?: boolean;
};

function Typeform({
  children,
  hideProgress,
  progressBarVariant = ProgressBarVariant.Default,
  showBonjoroBadge,
  onBonjoroBadgeClick,
  defaultIndex = 0,
  containerClassName,
  className,
  childClassName,
  transition = Transition.FadeIn,
  disableTabHotkey = false,
}: TypeformProps) {
  const steps = useMemo(() => children.length, [children]);
  const [index, { set }] = useCounter(defaultIndex);
  const step = useMemo(() => index + 1, [index]);
  const [validation, setValidation] = useState(Map<number, boolean>());

  const refs = useRef(children.map(() => createRef<HTMLElement>()));

  function isStepComplete(stepIndex: number) {
    /**
     * If step has no validation, the default should be to true.
     */
    return validation.get(stepIndex, true);
  }

  const defaultContainerClassName = [
    'h-full w-full flex-grow overflow-y-auto',
    'scrollbar-thin scrollbar-track-neutral-200 scrollbar-track-rounded-full',
    'scrollbar-thumb-neutral-400 scrollbar-thumb-rounded-full',
  ].join(' ');

  useEffect(() => {
    if (refs.current.length !== steps) {
      refs.current = children.map(() => createRef<HTMLElement>());
    }
  }, [children]);

  useInterval(() => {
    const newValidation = reduce(
      range(steps),
      (validation, i) => {
        let isValid = false;

        const form = get(refs, ['current', i, 'current']);
        if (form) {
          const inputs =
            form.querySelectorAll<HTMLInputElement>('input[required]');

          isValid = reduce<HTMLInputElement, boolean>(
            inputs,
            (isValid, input) => isValid && input.checkValidity(),
            true
          );
        }

        return validation.set(i, isValid);
      },
      validation
    );

    setValidation(newValidation);
  }, 250);

  function focusStepInput(stepIndex: number) {
    const element = get(refs, ['current', stepIndex, 'current']);

    if (element) {
      // focus the first input (if any)
      const input = element.querySelector<HTMLInputElement | HTMLButtonElement>(
        'input:not([type=hidden]):not(disabled), button:not([type=hidden]):not(disabled)'
      );

      if (input) {
        input.focus({
          preventScroll: true,
        });
      }
    }
  }

  const focusStepInputDebounced = useMemo(
    () => debounce(focusStepInput, 250),
    []
  );

  function scrollToStep(stepIndex: number, force?: boolean) {
    force = isBoolean(force) ? force : false;

    let targetIndex = stepIndex;

    if (stepIndex < 0) {
      targetIndex = 0;
    }
    if (!force && stepIndex > index) {
      // finds the last completed step and scrolls to the one after that

      // get all the steps between our current step and the intended one
      const stepsInRange = range(0, stepIndex + 1);

      // check their completion
      const firstIncompleteStep = Math.min(
        ...stepsInRange.filter((i) => !isStepComplete(i))
      );

      // Math.max will return -Infinity if there are no completed steps
      if (!isFinite(firstIncompleteStep)) {
        targetIndex = stepIndex;
      } else if (firstIncompleteStep >= 0) {
        targetIndex = firstIncompleteStep;
      } else {
        targetIndex = 0;
      }
    }

    const element = get(refs, ['current', targetIndex, 'current']);

    if (element) {
      const scrollTarget: Element = get(element, 'children.0') || element;

      scrollIntoView(scrollTarget, {
        block: 'center',
      });
    }
  }

  useEffect(() => {
    scrollToStep(index);
  }, []);

  useLayoutEffect(() => {
    refs.current.forEach(({ current }, i: number) => {
      if (!current) return;

      const inputs = current.querySelectorAll<
        HTMLInputElement | HTMLButtonElement
      >('input, button');

      inputs.forEach((input) => {
        input.tabIndex = i === index ? 0 : -1;
      });
    });

    focusStepInputDebounced(index);
  }, [index, validation]);

  const nextStep = useMemo(
    () => Math.min(steps - 1, index + 1),
    [steps, index]
  );
  const prevStep = useMemo(() => Math.max(0, index - 1), [index]);

  const scrollToNextStep = partial(scrollToStep, nextStep);
  const scrollToPrevStep = partial(scrollToStep, prevStep);

  if (!disableTabHotkey) {
    useHotkeys('Tab', () => scrollToNextStep());
    useHotkeys('Shift+Tab', () => scrollToPrevStep());
  }

  const handleScroll = throttle(() => {
    const hitbox = window.innerHeight / 2;
    // which step is in the hitbox of the screen
    refs.current.forEach((ref, i) => {
      const element = ref.current;
      if (element) {
        const { top, bottom } = element.getClientRects()[0];

        if (hitbox >= top && hitbox <= bottom) {
          if (index != i && (isStepComplete(index) || i < index)) {
            set(i);
          }
        }
      }
    });
  }, 16.667);

  const scrollContainerRef = useRef(null);
  useEventListener('scroll', handleScroll, scrollContainerRef);

  function handleStepClick(stepIndex: number) {
    if (index !== stepIndex) {
      scrollToStep(stepIndex);
    }
  }

  const { scrollDirection } = useScrollDirection(scrollContainerRef);

  const context = useMemo(
    () => ({
      scrollToNextStep,
      scrollToPrevStep,
      scrollDirection,
    }),
    [scrollToNextStep, scrollToPrevStep, scrollDirection]
  );

  const isHeightLimitReached = useMedia({
    maxHeight: 600,
  });

  return (
    <TypeformContext.Provider value={context}>
      <div
        ref={scrollContainerRef}
        className={cx(containerClassName, defaultContainerClassName)}
      >
        <FormContainer
          id='typeform-form-container'
          className={className}
          isLimitedHeight={isHeightLimitReached}
        >
          {children.map((child, i) => {
            return (
              <AnimatedChild
                key={i}
                visible={index === i}
                index={i}
                ref={refs.current[i]}
                completed={isStepComplete(i)}
                onClick={() => handleStepClick(i)}
                onNextStep={partial(scrollToStep, i + 1)}
                onPreviousStep={partial(scrollToStep, i - 1)}
                onScrollToStep={scrollToStep}
                onFocus={() => focusStepInput(i)}
                className={childClassName}
                transition={transition}
              >
                {child}
              </AnimatedChild>
            );
          })}
        </FormContainer>
      </div>
      {hideProgress ||
        (progressBarVariant === ProgressBarVariant.FullWidth ? (
          <FullWidthProgressBar
            stepsCount={steps}
            currentStep={step}
            showBonjoroBadge={showBonjoroBadge}
            onBonjoroBadgeClick={onBonjoroBadgeClick}
          />
        ) : (
          <ProgressBarWithNav
            completedSteps={index}
            stepsCount={steps}
            currentStep={step}
            isStepComplete={isStepComplete}
            onNextStep={scrollToNextStep}
            onPreviousStep={scrollToPrevStep}
          />
        ))}
    </TypeformContext.Provider>
  );
}

export default Typeform;
